/*
 * @Author: TDDC
 * @Date: 2026-02-25
 * @Description: ICAS登录扫码SDK插件
 * 接入文档: http://ibu-icas-sdk-check.sit.sf-express.com/
 * SDK支持三种环境: sit/uat/prod
 */

import Vue from 'vue'

/**
 * ICAS SDK加载管理
 */
class IcasSDKManager {
  constructor() {
    this.scriptUrl = ''
    this.isLoading = false
    this.loadPromise = null
    this.isLoaded = false
  }

  /**
   * 根据环境获取SDK URL
   * @param {string} env - 环境标识: 'sit', 'uat', 'prod'
   * @returns {string} SDK脚本URL
   */
  getScriptUrl(env) {
    // 规范化环境标识，将所有环境映射到正确的SDK URL
    // sit/dev/uat 环境都使用测试SDK
    // prod 环境使用生产SDK
    const normalizedEnv = env === 'prod' ? 'prod' : 'uat'
    
    const sdkUrlMap = {
      uat: 'https://ibu-icas-uat.sit.sf-express.com/icas-sdk/index.min.js',
      prod: 'https://ibu-icas.sf-express.com/icas-sdk/index.min.js',
    }
    
    const url = sdkUrlMap[normalizedEnv]
    console.log(`[ICAS SDK] Environment: ${env} -> ${normalizedEnv}, URL: ${url}`)
    return url
  }

  /**
   * 从 window.g.env 获取当前环境
   * @returns {string} 环境标识：'uat' 或 'prod'
   */
  getEnvironmentFromWindow() {
    if (typeof window !== 'undefined' && window.g && window.g.env) {
      const gEnv = window.g.env
      console.log(`[ICAS SDK] window.g.env: ${gEnv}`)
      // 从 window.g.env 映射到 SDK 环境
      // 'uat', 'sit', 'dev' 等非生产环境 -> 'uat' SDK
      // 'prd', 'prod' 等生产环境 -> 'prod' SDK
      return gEnv === 'prd' ? 'prod' : 'uat'
    }
    console.warn('[ICAS SDK] window.g.env not found, defaulting to uat')
    return 'uat'
  }

  /**
   * 动态加载SDK脚本
   * @param {string} url - 脚本URL
   * @returns {Promise} 加载完成的Promise
   */
  loadScript(url) {
    return new Promise((resolve, reject) => {
      if (this.isLoaded) {
        resolve()
        return
      }

      if (this.loadPromise) {
        return this.loadPromise.then(resolve).catch(reject)
      }

      this.isLoading = true

      this.loadPromise = new Promise((resolveLoad, rejectLoad) => {
        try {
          const script = document.createElement('script')
          script.src = url
          script.type = 'text/javascript'
          script.async = true

          script.onload = () => {
            this.isLoaded = true
            this.isLoading = false
            console.log('✅ ICAS SDK已加载成功')
            resolveLoad()
            resolve()
          }

          script.onerror = () => {
            this.isLoading = false
            console.error('❌ ICAS SDK加载失败')
            const error = new Error('Failed to load ICAS SDK')
            rejectLoad(error)
            reject(error)
          }

          document.body.appendChild(script)
        } catch (error) {
          this.isLoading = false
          console.error('❌ ICAS SDK加载异常:', error)
          rejectLoad(error)
          reject(error)
        }
      })

      return this.loadPromise.then(resolve).catch(reject)
    })
  }

  /**
   * 初始化并加载SDK
   * @param {string} env - 环境标识
   * @returns {Promise} 加载完成的Promise
   */
  async initialize(env) {
    if (this.isLoaded) {
      return Promise.resolve()
    }

    const url = this.getScriptUrl(env)
    return this.loadScript(url)
  }

  /**
   * 获取SDK实例
   * @returns {Object} window.IcasLoginDialog
   */
  getInstance() {
    if (typeof window !== 'undefined' && window.IcasLoginDialog) {
      return window.IcasLoginDialog
    }
    return null
  }
}

// 创建全局SDK管理器实例
const icasSDKManager = new IcasSDKManager()

// 注入Vue原型和Nuxt context
export default function ({ app, env }) {
  // 注入到Nuxt context
  app.$icasSDK = icasSDKManager

  // 注入到Vue原型，方便在组件中使用 this.$icasSDK
  Vue.prototype.$icasSDK = icasSDKManager

  // 在应用启动时初始化SDK
  // 优先级：window.g.env (static/env.js) > nuxtConfig.env > process.env > 默认值 'uat'
  // window.g.env 是从 static/env.js 中读取的全局环境配置，是最准确的环境信息
  let currentEnv = 'uat' // 默认值
  
  if (process.client) {
    // 第一优先：从 window.g.env 读取（最准确，来自 static/env.js）
    if (window.g && window.g.env) {
      const gEnv = window.g.env
      currentEnv = gEnv === 'prd' ? 'prod' : 'uat'
      console.log(`[ICAS SDK] ✅ Using window.g.env from static/env.js: ${gEnv} -> ${currentEnv}`)
    }
    // 第二优先：从 nuxt.config.js 的 env 对象读取
    else if (env?.BUILD_ENV || env?.CONFIG_ENV) {
      currentEnv = env?.BUILD_ENV || env?.CONFIG_ENV || 'uat'
      console.log(`[ICAS SDK] Using nuxt.config.js env: ${currentEnv}`)
    }
    // 第三优先：从 process.env 读取
    else if (process.env.BUILD_ENV || process.env.CONFIG_ENV) {
      currentEnv = process.env.BUILD_ENV || process.env.CONFIG_ENV || 'uat'
      console.log(`[ICAS SDK] Using process.env: ${currentEnv}`)
    }
    
    console.log(`[ICAS SDK] Final SDK environment: ${currentEnv}`)
    
    icasSDKManager.initialize(currentEnv).catch((error) => {
      console.error('ICAS SDK初始化失败:', error)
    })
  }
}
